<template>
  <el-menu
    class="sidebar-menu"
    :class="{ collapsed: props.collapsed }"
    :default-active="activeMenuPath"
    background-color="#ffffff"
    text-color="#666666"
    active-text-color="#5dc0f7"
    :unique-opened="false"
    :collapse="props.collapsed"
  >
    <!-- 有子菜单的项目 -->
    <template v-for="item in filteredMenus" :key="`parent-${item.name}`">
      <el-sub-menu
        v-if="item.children && item.children.length > 0"
        :index="item.name"
        class="main-menu-item"
      >
        <template #title>
          <el-icon class="menu-icon">
            <component :is="getIconComponent(item.icon)" />
          </el-icon>
          <span class="menu-item-text">{{ item.name }}</span>
        </template>

        <!-- 直接的二级菜单项目 -->
        <el-menu-item
          v-for="childItem in item.children"
          :key="`child-${childItem.name}`"
          :index="childItem.path || childItem.name"
          class="sub-menu-item"
          @click="handleMenuItemClick(childItem)"
        >
          <el-icon class="menu-icon">
            <component :is="getIconComponent(childItem.icon)" />
          </el-icon>
          <span>{{ childItem.name }}</span>
        </el-menu-item>
      </el-sub-menu>
    </template>

    <!-- 直接的一级菜单项目 -->
    <template v-for="item in filteredMenus" :key="`main-${item.name}`">
      <el-menu-item
        v-if="!item.children || item.children.length === 0"
        :index="item.path || item.name"
        class="main-menu-item"
        @click="handleMenuItemClick(item)"
      >
        <el-icon class="menu-icon">
          <component :is="getIconComponent(item.icon)" />
        </el-icon>
        <span class="menu-item-text">{{ item.name }}</span>
      </el-menu-item>
    </template>
  </el-menu>
</template>

<script setup lang="ts">
import { logger } from "@/utils/logger";
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import { normalizePermissions } from "@/utils/permissionMap";
import {
  House,
  TrendCharts,
  Warning,
  Document,
  Calendar,
  Monitor,
  DataAnalysis,
  Lock,
  Menu as MenuIcon,
  OfficeBuilding,
  DataBoard,
  DataLine,
  PieChart,
  Setting,
  User,
  Folder,
  InfoFilled,
  CircleClose,
  Memo,
  Cpu,
  VideoPlay,
  Bell,
} from "@element-plus/icons-vue";

interface MenuItem {
  name: string;
  path?: string;
  icon?: string;
  children?: MenuItem[];
  permissions?: string[];
  roles?: string[];
}

interface Props {
  collapsed?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  collapsed: false,
});

// 组合式API
const route = useRoute();
const router = useRouter();
const userStore = useUserStore();

// 响应式数据
const menus = ref<MenuItem[]>([]);

// 计算属性
const activeMenuPath = computed(() => route.path);

const filteredMenus = computed(() => {
  return filterMenusByPermissions(menus.value, userStore.user);
});

// 图标映射
const iconComponents: Record<string, any> = {
  dashboard: DataBoard,
  trending_up: TrendCharts,
  account_balance: OfficeBuilding,
  assessment: DataAnalysis,
  timeline: DataLine,
  insert_chart: PieChart,
  settings: Setting,
  security: Lock,
  people: User,
  description: Document,
  folder: Folder,
  list: MenuIcon,
  info: InfoFilled,
  warning: Warning,
  error: CircleClose,
  house: House,
  calendar: Calendar,
  monitor: Monitor,
  document: Document,
  memo: Memo,
  cpu: Cpu,
  videoplay: VideoPlay,
  bell: Bell,
  grid: DataBoard, // 添加 Grid 图标映射
};

/**
 * 获取图标组件
 */
const getIconComponent = (iconName?: string) => {
  if (!iconName) return MenuIcon;
  return iconComponents[iconName] || MenuIcon;
};

/**
 * 菜单项点击事件处理
 */
const handleMenuItemClick = (menuItem: MenuItem) => {
  if (menuItem.path) {
    router.push(menuItem.path);
  }
};

/**
 * 根据用户权限过滤菜单
 */
const filterMenusByPermissions = (menuItems: MenuItem[], user: any): MenuItem[] => {
  if (!menuItems) return [];
  const result: MenuItem[] = [];
  for (const item of menuItems) {
    if (item.children && item.children.length > 0) {
      const children = filterMenusByPermissions(item.children, user);
      if (children.length > 0) {
        result.push({ ...item, children });
      }
      continue; // 有 children 的 item 永远 continue，不 fall through 到 isMenuAllowed
    }
    if (isMenuAllowed(item, user)) {
      result.push(item);
    }
  }
  return result;
};

/**
 * 无需权限即可访问的路径白名单（所有登录用户可见）
 */
const NO_PERMISSION_PATHS: string[] = [];

/**
 * 检查菜单项是否允许访问
 * 策略：Default-Deny — 未配置权限的路由默认拒绝，仅白名单路由放行
 */
const isMenuAllowed = (menuItem: MenuItem, user: any): boolean => {
  if (!menuItem.path) return true;
  const { permission: required, roles } = getRequiredAccessForPath(menuItem.path);

  if (roles.length > 0 && roles.some((role) => userStore.hasRole(role))) {
    return true;
  }

  // 无权限配置时：仅白名单路由放行，其余拒绝（Default-Deny）
  if (!required) {
    return NO_PERMISSION_PATHS.includes(menuItem.path);
  }

  const normalized = normalizePermissions([required])[0];

  // 标准化失败时拒绝访问
  if (!normalized) {
    logger.warn(`权限标准化失败: ${required}, 路径: ${menuItem.path}`);
    return false;
  }

  try {
    // 直接检查权限（permissionMap 已覆盖 xxx_read_update → xxx_read 的映射）
    const hasPermission = !!userStore.hasPermission(normalized);

    return hasPermission;
  } catch (e) {
    logger.error("权限检查异常:", menuItem.path, e);
    return false;
  }
};

// 获取路径对应的权限/角色要求：优先读取路由 meta
const getRequiredAccessForPath = (
  path?: string,
): { permission: string | null; roles: string[] } => {
  if (!path) return { permission: null, roles: [] };
  const route = router.getRoutes().find((r) => r.path === path);
  const perms = (route?.meta as any)?.permissions;
  const roles = (route?.meta as any)?.roles;
  // 只返回路由中明确配置的权限，不再从路径推导
  return {
    permission: Array.isArray(perms) && perms.length > 0 ? perms[0] : null,
    roles: Array.isArray(roles) ? roles : [],
  };
};

/**
 * 初始化菜单数据（完全匹配老项目结构）
 */
const initializeMenus = () => {
  // 从router中导入完整菜单配置
  import("@/router").then(({ menus: routerMenus }) => {
    menus.value = routerMenus.map((item) => ({
      ...item,
      icon: getIconName(item.icon),
    }));
  });
};

/**
 * 将路由图标名转换为组件图标名
 */
const getIconName = (iconName?: string): string => {
  const iconMap: Record<string, string> = {
    Warning: "warning",
    Tools: "trending_up",
    Calendar: "calendar",
    Monitor: "monitor",
    Wallet: "account_balance",
    Lock: "security",
    TrendCharts: "trending_up",
    List: "list",
    Document: "document",
    Grid: "grid", // 添加 Grid 映射
    Setting: "settings", // 添加 Setting 映射
    Cpu: "cpu", // 添加 Cpu 映射
    Memo: "memo", // 添加 Memo 映射
    VideoPlay: "videoplay", // 添加 VideoPlay 映射
    Bell: "bell",
    DataAnalysis: "assessment", // 添加 DataAnalysis 映射
    Compass: "explore", // 基差工作台
    SetUp: "tune", // 主品种基差阈值
    Link: "link", // 账户对配置
  };
  return iconMap[iconName || ""] || "list";
};

/**
 * 从路径生成权限名
 */
const getPermissionFromPath = (path?: string): string => {
  if (!path) return "";
  const pathParts = path.split("/");
  const lastPart = pathParts[pathParts.length - 1];
  return `${lastPart}_read`;
};

// 组件挂载时初始化
onMounted(() => {
  initializeMenus();
});
</script>

<style lang="scss" scoped>
/* Element Plus 菜单现代化样式 */

.sidebar-menu {
  border: none;
  height: 100%;
  width: 100%;
  min-width: 0;

  :deep(.menu-item-text) {
    font-size: 16px;
    font-family: "Microsoft YaHei", sans-serif;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :deep(.menu-icon) {
    margin-right: 8px;
    font-size: 18px;
    width: 18px;
    text-align: center;
  }

  /* 主菜单项样式 */
  :deep(.main-menu-item) {
    .el-menu-item,
    .el-sub-menu__title {
      height: 50px;
      line-height: 50px;
      padding-left: 20px !important;
      overflow: hidden;

      &:hover {
        background-color: rgba(93, 192, 247, 0.1) !important;
        color: #5dc0f7 !important;
      }

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  /* 子菜单项样式 */
  :deep(.sub-menu-item) {
    .el-menu-item {
      height: 45px;
      line-height: 45px;
      padding-left: 40px !important;
      overflow: hidden;

      &:hover {
        background-color: rgba(93, 192, 247, 0.08) !important;
      }

      span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  /* 三级菜单项样式 */
  :deep(.grandchild-menu-item) {
    height: 40px;
    line-height: 40px;
    padding-left: 60px !important;
    overflow: hidden;

    &:hover {
      background-color: rgba(93, 192, 247, 0.06) !important;
    }

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  /* 激活状态样式 */
  :deep(.el-menu-item.is-active) {
    background-color: rgba(93, 192, 247, 0.15) !important;
    color: #5dc0f7 !important;
    border-right: 3px solid #5dc0f7;

    .menu-icon {
      color: #5dc0f7 !important;
    }
  }

  /* 子菜单标题样式 */
  :deep(.el-sub-menu__title) {
    color: #666 !important;

    &:hover {
      color: #5dc0f7 !important;

      .menu-icon {
        color: #5dc0f7 !important;
      }
    }
  }

  /* 展开的子菜单样式 */
  :deep(.el-sub-menu.is-opened > .el-sub-menu__title) {
    color: #5dc0f7 !important;

    .menu-icon {
      color: #5dc0f7 !important;
    }
  }

  /* 菜单项文字颜色 */
  :deep(.el-menu-item) {
    color: #666 !important;

    &:hover {
      color: #5dc0f7 !important;

      .menu-icon {
        color: #5dc0f7 !important;
      }
    }
  }

  /* 移除Element Plus默认的边框和阴影 */
  :deep(.el-sub-menu .el-menu) {
    background-color: #f8f9fa;
  }

  /* 现代化的间距和过渡效果 */
  :deep(.el-menu-item),
  :deep(.el-sub-menu__title) {
    transition: all 0.3s ease;
    border-radius: 0;
  }
}

/* 整体容器样式 */
.sidebar-menu.el-menu {
  background-color: #ffffff;
  width: 100%;

  &.collapsed {
    :deep(.menu-item-text) {
      display: none;
    }

    :deep(.el-sub-menu__title span) {
      display: none;
    }

    :deep(.el-sub-menu__icon-arrow) {
      display: none;
    }
  }
}
</style>
